import { useEffect, useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Loader2, MessagesSquare, CheckSquare } from 'lucide-react'
import { chatService } from '../../services/chatService'
import { useElementWidth } from '../../hooks/useElementWidth'
import { toast } from '../../stores/uiStore'
import MessageRow from './MessageRow'
import Composer from './Composer'
import { jumpToMessage } from './messageJump'
import { dropOutgoingMessage, mergeThreadOutgoing, showOutgoingMessage } from './outgoingMessage'

// Konu dizisi (thread) — sağ panel: kök mesaj + yanıtlar + yanıt kutusu.
// Açılır/kapanır: parent (ChatPage / ChatDock) yan yana gösterir; X ile kapanır.
export default function ThreadPanel({ company, rootId, channelId, channelName, myUserId, channelKind, onClose, onChanged, onOpenProfile, onCreateTask, onMessageAction, onDeleteMessage,
  // Aramadan/kaydedilenlerden gelen KONU YANITI burada vurgulanır: kök mesajı
  // açmak yetmiyor, kullanıcı aradığı yanıtın hangisi olduğunu görmeli.
  highlightId,
  // Genişlik parent'tan (sürüklenebilir ayırıcı) gelir; yoksa sabit sütun
  width,
  // Panelin sunamadığı aksiyonlar gizlenir (ör. belge görüşmesinde kanal asistanı
  // yok) — sunulamayan düğmeyi çizmek, kullanıcıya çalışmayan bir şey vaat eder.
  hideActions,
  className = 'shrink-0 border-l border-gray-100' }) {
  const [alsoChannel, setAlsoChannel] = useState(false)
  const panelRef = useRef(null)
  const listRef = useRef(null)
  const panelWidth = useElementWidth(panelRef)   // konu paneli de sürüklenip genişleyebiliyor
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['chat-thread', rootId],
    queryFn: () => chatService.listThread(company, rootId),
    enabled: !!rootId,
  })
  const msgs = mergeThreadOutgoing(rootId, data?.data?.messages || [])
  const taskRefs = data?.data?.task_refs || {}
  const mentionRefs = data?.data?.mention_refs || {}
  const root = msgs[0]
  const replies = msgs.slice(1)

  // Vurgulanan yanıta kaydır. Yanıtlar YÜKLENDİKTEN sonra çalışmalı; konu uzunsa
  // aranan yanıt görünür alanın dışında kalıyor ve kullanıcı "gitmedi" sanıyor.
  useEffect(() => {
    if (!highlightId || !msgs.length) return
    const node = listRef.current?.querySelector(`[data-mid="${highlightId}"]`)
    node?.scrollIntoView({ block: 'center', behavior: 'smooth' })
  }, [highlightId, msgs])
  const err = (e) => toast.error('Hata', e.response?.data?.detail || e.message)
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['chat-thread', rootId] })
    onChanged?.()
  }

  // Düzenleme AYRI popup değil, konunun kendi yazma kutusuna düşer (kanal görünümüyle
  // aynı davranış). Diğer aksiyonlar üst bileşene delege edilir.
  const [editing, setEditing] = useState(null)   // {id, body}
  // Alıntılı yanıt konu içinde de çalışmalı: aksiyon çubuğundaki “Alıntıla”
  // düğmesi MessageRow'da `onReply` çağırıyor; bu prop konu panelinde HİÇ
  // verilmediği için düğme görünüyor ama tıklayınca hiçbir şey olmuyordu
  // (16.09.2026 kullanıcı bildirimi). Sunucu tarafı zaten hazırdı —
  // `quote_msg_id` her mesajda desteklenir, konu yanıtında da.
  const [replyTo, setReplyTo] = useState(null)
  useEffect(() => { setEditing(null); setReplyTo(null) }, [rootId])
  const handleAction = (m, action, ev) => {
    if (action === 'edit') { setEditing({ id: m.id, body: m.body || '' }); return }
    onMessageAction?.(m, action, ev)
  }

  const mReply = useMutation({
    mutationFn: async (payload) => {
      const files = payload._files
      // Yalnız ek (fotoğraf/dosya) gönderiminde gövde boştur → allow_empty sunucuya
      // iletilmeli; eskiden düşürülüyordu ve konu yanıtında "Boş mesaj" hatası çıkıyordu.
      const body = { body: payload.body, post_type: payload.post_type, post_title: payload.post_title,
                     ...(payload.allow_empty || (!(payload.body || '').trim() && files?.length) ? { allow_empty: true } : {}),
                     ...(payload.quote_msg_id ? { quote_msg_id: payload.quote_msg_id } : {}) }
      const r = await chatService.sendMessage(company, channelId, { ...body, reply_to_id: rootId })
      const mid = r?.data?.id
      if (mid && files?.length) {
        for (const f of files) {
          try { await chatService.uploadAttachment(company, mid, f) } catch (e) { err(e) }
        }
      }
      // "Kanala da gönder" — aynı içeriği ana akışa da yolla
      if (alsoChannel) {
        try {
          const r2 = await chatService.sendMessage(company, channelId, body)
          const mid2 = r2?.data?.id
          if (mid2 && files?.length) {
            for (const f of files) {
              try { await chatService.uploadAttachment(company, mid2, f) } catch (e) { err(e) }
            }
          }
        } catch (e) { err(e) }
      }
      return r
    },
    onMutate: (payload) => {
      const tempId = showOutgoingMessage(qc, {
        channelId, payload, threadRootId: rootId, alsoChannel,
      })
      return { tempId }
    },
    // Gönderdikten SONRA konu listesi tazelenmeli. Eskiden yalnız onError vardı; yanıt
    // sunucuya yazılıyor ama panelde GÖRÜNMÜYORDU. /chat sayfasında SSE (useChatStream)
    // araya girip tazelediği için fark edilmiyordu — yüzen panelde stream yok, yanıt
    // "kaybolmuş" gibi duruyordu (25.08.2026 kullanıcı bildirimi). Kendi gönderdiğini
    // göstermek için başka bir mekanizmaya güvenilmez.
    onSuccess: refresh,
    onError: (e, _v, ctx) => {
      dropOutgoingMessage(qc, { channelId, threadRootId: rootId, tempId: ctx?.tempId })
      err(e)
    },
  })
  const mReact = useMutation({
    mutationFn: ({ messageId, emoji }) => chatService.toggleReaction(company, messageId, emoji),
    onSuccess: refresh, onError: err,
  })

  const style = width != null ? { ['--thread-w']: `${width}px` } : undefined
  const widthCls = width != null
    ? 'w-full lg:w-[var(--thread-w)] lg:min-w-[var(--thread-w)] lg:max-w-[var(--thread-w)]'
    : 'w-full lg:w-[380px]'
  return (
    <div className={`${className} ${widthCls} bg-gray-50/40 flex flex-col overflow-hidden animate-[threadin_.18s_ease-out]`}
      style={style}>
      <div className="px-4 py-2.5 border-b border-gray-100 flex items-center gap-2 bg-white shrink-0">
        <MessagesSquare size={15} className="text-violet-500" />
        <span className="text-sm font-semibold text-gray-800 flex-1 truncate">{root?.author_name ? `${root.author_name} · konu` : 'Konu dizisi'}</span>
        <span className="text-[11px] text-gray-400">{replies.length} yanıt</span>
        <button onClick={onClose} title="Konuyu kapat" className="p-1 text-gray-400 hover:text-gray-700"><X size={16} /></button>
      </div>
      {onCreateTask && (
        <div className="px-3 py-1.5 border-b border-gray-100 bg-white shrink-0">
          <button onClick={onCreateTask}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border border-gray-200 text-[12px] text-gray-700 hover:bg-gray-50">
            <CheckSquare size={13} className="text-violet-500" /> Görev oluştur
          </button>
        </div>
      )}
      <div ref={listRef} className="flex-1 overflow-y-auto px-2 py-2">
        {isLoading ? (
          <div className="py-8 text-center text-gray-400"><Loader2 size={16} className="animate-spin mx-auto" /></div>
        ) : (
          <>
            {root && (
              <div className="bg-white rounded-xl border border-gray-100 mb-2">
                {/* Araç çubuğu aksiyonları (Göreve bağla, kaydet, ata, çözüldü, ilet, kopyala…)
                    kanal görünümüyle AYNI handler'a bağlı. Eskiden yalnız onReact veriliyordu:
                    düğmeler görünüyor ama tıklayınca HİÇBİR ŞEY olmuyordu (onEdit undefined). */}
                <MessageRow message={root} taskRefs={taskRefs} mentionRefs={mentionRefs} myUserId={myUserId} company={company} channelKind={channelKind} onOpenProfile={onOpenProfile} inThread rowWidth={panelWidth}
                  onEdit={handleAction} onDelete={onDeleteMessage}
                  onReply={setReplyTo} onQuoteJump={jumpToMessage} hideActions={hideActions}
                  onReact={(mid, emoji) => emoji && mReact.mutate({ messageId: mid, emoji })} />
              </div>
            )}
            {replies.map((m) => (
              <MessageRow key={m.id} message={m} taskRefs={taskRefs} mentionRefs={mentionRefs} myUserId={myUserId} company={company} channelKind={channelKind} onOpenProfile={onOpenProfile} inThread rowWidth={panelWidth}
                highlight={String(highlightId || '') === String(m.id)}
                onEdit={handleAction} onDelete={onDeleteMessage}
                onReply={setReplyTo} onQuoteJump={jumpToMessage} hideActions={hideActions}
                onReact={(mid, emoji) => emoji && mReact.mutate({ messageId: mid, emoji })} />
            ))}
            {replies.length === 0 && <div className="py-6 text-center text-xs text-gray-400">İlk yanıtı sen yaz.</div>}
          </>
        )}
      </div>
      <div className="border-t border-gray-100 bg-white">
        <label className="flex items-center gap-2 px-4 pt-2 text-[12px] text-gray-600 cursor-pointer">
          <input type="checkbox" checked={alsoChannel} onChange={(e) => setAlsoChannel(e.target.checked)} className="accent-violet-600" />
          Kanala da gönder{channelName ? ` (#${channelName})` : ''}
        </label>
        <Composer company={company} channelId={channelId} compact placeholder="Konuya yanıt yaz…"
          editing={editing} onCancelEdit={() => setEditing(null)}
          replyTo={replyTo} onCancelReply={() => setReplyTo(null)}
          onSaveEdit={async (id, val) => {
            try { await chatService.editMessage(company, id, val); setEditing(null); refresh(); toast.success('Güncellendi') } catch (e) { err(e) }
          }}
          onSend={(p, files) => {
            // Bandı ÖNCE temizle (kanal görünümüyle aynı gerekçe): gönderim
            // yavaşsa ikinci yanıt yanlışlıkla aynı alıntıyla gitmesin.
            const q = replyTo?.id
            if (q) setReplyTo(null)
            mReply.mutate({ ...p, _files: files, ...(q ? { quote_msg_id: q } : {}) })
          }} />
      </div>
    </div>
  )
}
